import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, ScrollText, ChevronLeft, ChevronRight } from "lucide-react";

const ACTION_LABEL = {
  create: "Ekleme", update: "Güncelleme", delete: "Silme", revise: "Revize",
  send_email: "E-posta", download: "PDF İndirme", login: "Giriş", approve_below_cost: "Alış Altı Onay",
};
const ACTION_COLOR = {
  create: "bg-emerald-100 text-emerald-700", update: "bg-blue-100 text-blue-700",
  delete: "bg-rose-100 text-rose-700", revise: "bg-amber-100 text-amber-700",
  send_email: "bg-purple-100 text-purple-700", download: "bg-slate-100 text-slate-700",
  login: "bg-slate-100 text-slate-600", approve_below_cost: "bg-amber-200 text-amber-900",
};
const ENTITY_LABEL = { customer: "Müşteri", product: "Ürün", quote: "Teklif", staff: "Personel", settings: "Ayarlar", auth: "Oturum" };
const PAGE_SIZE = 25;

export default function Logs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [userId, setUserId] = useState("all");
  const [action, setAction] = useState("all");
  const [entity, setEntity] = useState("all");
  const [page, setPage] = useState(0);
  const [retention, setRetention] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/logs", {
        params: {
          q, user_id: userId === "all" ? "" : userId, action: action === "all" ? "" : action,
          entity_type: entity === "all" ? "" : entity, skip: page * PAGE_SIZE, limit: PAGE_SIZE,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [q, userId, action, entity, page]);

  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/settings").then((r) => setRetention(r.data.log_retention_days || 0)).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(0); }, [q, userId, action, entity]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <PageHeader
        title="İşlem Geçmişi"
        subtitle={`Personelin sistemde yaptığı tüm işlemlerin kaydı. ${retention > 0 ? `Kayıtlar ${retention} gün saklanır.` : "Kayıtlar süresiz saklanır."}`}
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Açıklama ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="log-search" />
        </div>
        <Select value={userId} onValueChange={setUserId}>
          <SelectTrigger data-testid="log-filter-user"><SelectValue placeholder="Personel" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm Personel</SelectItem>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger data-testid="log-filter-action"><SelectValue placeholder="İşlem" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm İşlemler</SelectItem>{Object.entries(ACTION_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger data-testid="log-filter-entity"><SelectValue placeholder="Kayıt Türü" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm Türler</SelectItem>{Object.entries(ENTITY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div> : items.length === 0 ? (
        <Card className="p-12 text-center border-slate-100 rounded-2xl card-soft"><ScrollText className="w-10 h-10 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">Kayıt bulunamadı.</p></Card>
      ) : (
        <>
          <Card className="border-slate-100 rounded-2xl card-soft divide-y divide-slate-100 overflow-hidden">
            {items.map((l) => (
              <div key={l.id} className="flex items-start gap-4 p-4" data-testid={`log-row-${l.id}`}>
                <Badge className={`${ACTION_COLOR[l.action] || "bg-slate-100 text-slate-700"} border-0 shrink-0`}>{ACTION_LABEL[l.action] || l.action}</Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800">{l.description}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    <span className="font-medium text-slate-600">{l.user_name}</span> · {ENTITY_LABEL[l.entity_type] || l.entity_type} · {(l.created_at || "").slice(0, 16).replace("T", " ")}
                  </p>
                </div>
              </div>
            ))}
          </Card>
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-slate-500" data-testid="log-count">Toplam {total} kayıt · Sayfa {page + 1}/{totalPages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-xl" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="log-prev"><ChevronLeft className="w-4 h-4 mr-1" />Önceki</Button>
              <Button variant="outline" size="sm" className="rounded-xl" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="log-next">Sonraki<ChevronRight className="w-4 h-4 ml-1" /></Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
